import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, User, Mail, Phone, Clock, DollarSign, Award, Plus, Trash2 } from 'lucide-react';
import type { TeamMember, WeeklySchedule } from '@/types';

interface TeamMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (memberData: Omit<TeamMember, 'id' | 'providerId' | 'createdAt' | 'updatedAt'>) => void;
  member?: TeamMember;
  mode: 'add' | 'edit';
}

export function TeamMemberModal({ isOpen, onClose, onSave, member, mode }: TeamMemberModalProps) {
  const [formData, setFormData] = useState({
    userId: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    role: 'TECHNICIAN' as TeamMember['role'],
    skills: [] as string[],
    certifications: [] as string[],
    hourlyRate: 0,
    isActive: true,
    hireDate: new Date(),
    availability: {
      monday: { startTime: '08:00', endTime: '17:00', available: true, timeSlotDuration: 60, bufferTime: 15 },
      tuesday: { startTime: '08:00', endTime: '17:00', available: true, timeSlotDuration: 60, bufferTime: 15 },
      wednesday: { startTime: '08:00', endTime: '17:00', available: true, timeSlotDuration: 60, bufferTime: 15 },
      thursday: { startTime: '08:00', endTime: '17:00', available: true, timeSlotDuration: 60, bufferTime: 15 },
      friday: { startTime: '08:00', endTime: '17:00', available: true, timeSlotDuration: 60, bufferTime: 15 },
      saturday: { startTime: '09:00', endTime: '15:00', available: false, timeSlotDuration: 60, bufferTime: 15 },
      sunday: { startTime: '10:00', endTime: '14:00', available: false, timeSlotDuration: 60, bufferTime: 15 }
    } as WeeklySchedule
  });

  const [newSkill, setNewSkill] = useState('');
  const [newCertification, setNewCertification] = useState('');

  useEffect(() => {
    if (member && mode === 'edit') {
      setFormData({
        userId: member.userId,
        firstName: member.firstName,
        lastName: member.lastName,
        email: member.email,
        phone: member.phone,
        role: member.role,
        skills: [...member.skills],
        certifications: [...member.certifications],
        hourlyRate: member.hourlyRate || 0,
        isActive: member.isActive,
        hireDate: member.hireDate,
        availability: { ...member.availability }
      });
    } else if (mode === 'add') {
      // Reset form for new member
      setFormData({
        userId: `user-${Date.now()}`,
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        role: 'TECHNICIAN',
        skills: [],
        certifications: [],
        hourlyRate: 25,
        isActive: true,
        hireDate: new Date(),
        availability: {
          monday: { startTime: '08:00', endTime: '17:00', available: true, timeSlotDuration: 60, bufferTime: 15 },
          tuesday: { startTime: '08:00', endTime: '17:00', available: true, timeSlotDuration: 60, bufferTime: 15 },
          wednesday: { startTime: '08:00', endTime: '17:00', available: true, timeSlotDuration: 60, bufferTime: 15 },
          thursday: { startTime: '08:00', endTime: '17:00', available: true, timeSlotDuration: 60, bufferTime: 15 },
          friday: { startTime: '08:00', endTime: '17:00', available: true, timeSlotDuration: 60, bufferTime: 15 },
          saturday: { startTime: '09:00', endTime: '15:00', available: false, timeSlotDuration: 60, bufferTime: 15 },
          sunday: { startTime: '10:00', endTime: '14:00', available: false, timeSlotDuration: 60, bufferTime: 15 }
        }
      });
    }
  }, [member, mode, isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
    onClose();
  };

  const addSkill = () => {
    if (newSkill.trim() && !formData.skills.includes(newSkill.trim())) {
      setFormData(prev => ({
        ...prev,
        skills: [...prev.skills, newSkill.trim()]
      }));
      setNewSkill('');
    }
  };

  const removeSkill = (skillToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      skills: prev.skills.filter(skill => skill !== skillToRemove)
    }));
  };

  const addCertification = () => {
    if (newCertification.trim() && !formData.certifications.includes(newCertification.trim())) {
      setFormData(prev => ({
        ...prev,
        certifications: [...prev.certifications, newCertification.trim()]
      }));
      setNewCertification('');
    }
  };

  const removeCertification = (certToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      certifications: prev.certifications.filter(cert => cert !== certToRemove)
    }));
  };

  const updateAvailability = (day: keyof WeeklySchedule, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      availability: {
        ...prev.availability,
        [day]: {
          ...prev.availability[day],
          [field]: value
        }
      }
    }));
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">
            {mode === 'add' ? 'Add Team Member' : 'Edit Team Member'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <User className="w-4 h-4 inline mr-2" />
                First Name
              </label>
              <input
                type="text"
                required
                value={formData.firstName}
                onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                className="input input-bordered w-full"
                placeholder="Enter first name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Last Name
              </label>
              <input
                type="text"
                required
                value={formData.lastName}
                onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                className="input input-bordered w-full"
                placeholder="Enter last name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Mail className="w-4 h-4 inline mr-2" />
                Email
              </label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                className="input input-bordered w-full"
                placeholder="Enter email address"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Phone className="w-4 h-4 inline mr-2" />
                Phone
              </label>
              <input
                type="tel"
                required
                value={formData.phone}
                onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                className="input input-bordered w-full"
                placeholder="Enter phone number"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Role
              </label>
              <select
                value={formData.role}
                onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value as TeamMember['role'] }))}
                className="select select-bordered w-full"
              >
                <option value="TECHNICIAN">Technician</option>
                <option value="MANAGER">Manager</option>
                <option value="APPRENTICE">Apprentice</option>
                <option value="ADMIN">Admin</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <DollarSign className="w-4 h-4 inline mr-2" />
                Hourly Rate (€)
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={formData.hourlyRate}
                onChange={(e) => setFormData(prev => ({ ...prev, hourlyRate: parseFloat(e.target.value) || 0 }))}
                className="input input-bordered w-full"
                placeholder="Enter hourly rate"
              />
            </div>
          </div>

          {/* Skills */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Skills
            </label>
            <div className="flex gap-2 mb-3">
              <input
                type="text"
                value={newSkill}
                onChange={(e) => setNewSkill(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSkill())}
                className="input input-bordered flex-1"
                placeholder="Add a skill"
              />
              <button
                type="button"
                onClick={addSkill}
                className="btn btn-outline"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {formData.skills.map((skill, index) => (
                <span
                  key={index}
                  className="badge badge-outline flex items-center gap-1"
                >
                  {skill}
                  <button
                    type="button"
                    onClick={() => removeSkill(skill)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          </div>

          {/* Certifications */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Award className="w-4 h-4 inline mr-2" />
              Certifications
            </label>
            <div className="flex gap-2 mb-3">
              <input
                type="text"
                value={newCertification}
                onChange={(e) => setNewCertification(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCertification())}
                className="input input-bordered flex-1"
                placeholder="Add a certification"
              />
              <button
                type="button"
                onClick={addCertification}
                className="btn btn-outline"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {formData.certifications.map((cert, index) => (
                <span
                  key={index}
                  className="badge badge-success flex items-center gap-1"
                >
                  {cert}
                  <button
                    type="button"
                    onClick={() => removeCertification(cert)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          </div>

          {/* Availability */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-4">
              <Clock className="w-4 h-4 inline mr-2" />
              Weekly Availability
            </label>
            <div className="space-y-3">
              {Object.entries(formData.availability).map(([day, schedule]) => (
                <div key={day} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                  <div className="w-20">
                    <span className="font-medium capitalize">{day}</span>
                  </div>
                  
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={schedule.available}
                      onChange={(e) => updateAvailability(day as keyof WeeklySchedule, 'available', e.target.checked)}
                      className="checkbox checkbox-sm mr-2"
                    />
                    Available
                  </label>

                  {schedule.available && (
                    <>
                      <div className="flex items-center gap-2">
                        <input
                          type="time"
                          value={schedule.startTime}
                          onChange={(e) => updateAvailability(day as keyof WeeklySchedule, 'startTime', e.target.value)}
                          className="input input-bordered input-sm"
                        />
                        <span>to</span>
                        <input
                          type="time"
                          value={schedule.endTime}
                          onChange={(e) => updateAvailability(day as keyof WeeklySchedule, 'endTime', e.target.value)}
                          className="input input-bordered input-sm"
                        />
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Status */}
          <div>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                className="checkbox mr-2"
              />
              <span className="text-sm font-medium text-gray-700">Active Member</span>
            </label>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-6 border-t">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-outline"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
            >
              {mode === 'add' ? 'Add Member' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  if (typeof document === 'undefined') {
    return modalContent;
  }

  return createPortal(modalContent, document.body);
}